import { Button, Dialog, GlobalStatus, H1, P, Table, Td, Th, Tr } from '@dnb/eufemia';
import { reset as SyncIcon } from '@dnb/eufemia/icons';
import { type JSX, useCallback, useEffect, useState } from 'react';

import { backendClient } from '@/api/backendClient';
import BackButton from '@/components/BackButton';
import Container from '@/components/Container';
import LoadingModal from '@/components/LoadingModal';
import { EmbeddingCountResponse, SyncResultResponse } from '@/openapi/schema';

type EmbeddingType = EmbeddingCountResponse['entityType'];

const entityTypeLabels: Record<EmbeddingType, string> = {
  app: 'App',
  team: 'Team',
  content: 'Content',
  api: 'API',
  guide: 'Guide',
  api_reference: 'API Reference',
  scope: 'Scope',
  event: 'Event',
};

function formatEntityType(entityType: EmbeddingType): string {
  return entityTypeLabels[entityType];
}

export default function EmbeddingsPage(): JSX.Element {
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState<EmbeddingType | null>(null);
  const [results, setResults] = useState<SyncResultResponse[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState<EmbeddingCountResponse[]>([]);
  const [countsLoading, setCountsLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<EmbeddingType | null>(null);

  const fetchCounts = useCallback(async () => {
    setCountsLoading(true);
    try {
      const { data, error: apiError } = await backendClient.GET('/embeddings/counts');
      if (apiError) {
        setError('Failed to fetch embedding counts.');
        return;
      }
      setCounts(data ?? []);
    } catch {
      setError('An unexpected error occurred while fetching embedding counts.');
    } finally {
      setCountsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setError(null);
    setResults(null);

    try {
      const { data, error: apiError } = await backendClient.POST('/embeddings/sync');

      if (apiError) {
        setError('Failed to sync embeddings. Please try again.');
        return;
      }

      setResults(data ?? []);
      await fetchCounts();
    } catch {
      setError('An unexpected error occurred while syncing embeddings.');
    } finally {
      setSyncing(false);
    }
  }, [fetchCounts]);

  const handleDeleteAndSync = useCallback(
    async (embeddingType: EmbeddingType) => {
      setDeleting(embeddingType);
      setError(null);
      setResults(null);
      setDeleteConfirm(null);

      try {
        const { error: deleteError } = await backendClient.DELETE('/embeddings/{embeddingType}', {
          params: { path: { embeddingType } },
        });

        if (deleteError) {
          setError(`Failed to delete embeddings for ${embeddingType}.`);
          return;
        }

        const { data, error: syncError } = await backendClient.POST('/embeddings/sync');

        if (syncError) {
          setError(`Deleted ${embeddingType} embeddings but failed to sync.`);
          await fetchCounts();
          return;
        }

        setResults(data ?? []);
        await fetchCounts();
      } catch {
        setError(`An unexpected error occurred while deleting and syncing ${embeddingType} embeddings.`);
      } finally {
        setDeleting(null);
      }
    },
    [fetchCounts],
  );

  return (
    <div>
      {(syncing || deleting) && <LoadingModal />}
      <BackButton to="/">Home</BackButton>
      <Container centered>
        <H1 top="large">Embeddings</H1>
        <P top="medium">
          This is the manual sync page for embeddings. Embeddings are also automatically synced every hour between 5 AM
          and 8 PM (Norway time). Use this page to trigger a manual sync or to delete and re-sync specific embedding
          types when needed.
        </P>
        <Button
          disabled={syncing || !!deleting}
          icon="refresh"
          icon_position="left"
          on_click={handleSync}
          top="large"
          variant="secondary"
        >
          {syncing ? 'Syncing...' : 'Sync Embeddings'}
        </Button>

        {error && <GlobalStatus id="embeddings-error" show state="error" text={error} top="large" />}

        {!countsLoading && counts.length > 0 && (
          <div>
            <H1 size="medium" top="large">
              Embedding Counts
            </H1>
            <Table top="small">
              <thead>
                <Tr>
                  <Th>Entity Type</Th>
                  <Th>Count</Th>
                  <Th>Actions</Th>
                </Tr>
              </thead>
              <tbody>
                {counts.map((item) => (
                  <Tr key={item.entityType}>
                    <Td>{formatEntityType(item.entityType)}</Td>
                    <Td>{item.count}</Td>
                    <Td>
                      <Button
                        disabled={syncing || !!deleting}
                        icon={SyncIcon}
                        icon_position="left"
                        on_click={() => setDeleteConfirm(item.entityType)}
                      >
                        Delete &amp; Re-sync
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {deleteConfirm && (
          <Dialog
            confirmText="Delete &amp; Re-sync"
            confirmType="warning"
            icon={SyncIcon}
            onConfirm={() => handleDeleteAndSync(deleteConfirm)}
            onDecline={() => setDeleteConfirm(null)}
            openState
            title={`Delete & re-sync ${formatEntityType(deleteConfirm)} embeddings`}
            triggerAttributes={{ hidden: true }}
            variant="confirmation"
          >
            <P>
              Are you sure you want to delete all <strong>{formatEntityType(deleteConfirm)}</strong> embeddings and
              re-sync? This will delete the existing embeddings and regenerate them.
            </P>
          </Dialog>
        )}

        {results && (
          <Dialog
            onClose={() => setResults(null)}
            openState
            title="Sync Results"
            triggerAttributes={{ hidden: true }}
            variant="information"
          >
            <Table>
              <thead>
                <Tr>
                  <Th>Entity Type</Th>
                  <Th>Saved</Th>
                  <Th>Failed</Th>
                </Tr>
              </thead>
              <tbody>
                {results.map((result) => (
                  <Tr key={result.entityType}>
                    <Td>{formatEntityType(result.entityType)}</Td>
                    <Td>{result.saved}</Td>
                    <Td>{result.failed}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Dialog>
        )}
      </Container>
    </div>
  );
}
